import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Button, Combobox } from '@aeris/ui'

import { fetchProducts } from '../lib/api.ts'
import { useDebounced } from '../lib/use-debounced.ts'

/**
 * Escolher VÁRIOS produtos — "quem comprou ar-condicionado OU ventilador"
 * (F060).
 *
 * **Produto, não variante.** É o oposto de `ProductPicker`
 * (`product-picker.tsx`), que existe para a venda: uma linha de venda
 * referencia a VARIANTE, porque é ela que tem preço, código de barras e
 * saldo. Uma campanha é sobre o produto: quem comprou o ar-condicionado
 * entra, tenha levado a versão de 9.000 ou a de 12.000 BTUs. A consulta do
 * servidor sobe de `sales_document_line.variant_id` para
 * `product_variant.product_id` justamente por isso (`matchingParties`,
 * `@aeris/database`).
 *
 * O que já foi escolhido vira marcador removível ABAIXO do campo e some dos
 * resultados: oferecer de novo o que já está na lista é convidar ao clique
 * que não faz nada. O NOME fica guardado junto do id porque a busca seguinte
 * não traz mais o produto anterior — sem isso o marcador viraria um UUID
 * assim que a pessoa digitasse outra palavra. É a mesma armadilha que
 * `ProductPicker` documenta no seu `selectedLabel`.
 */

export type SelectedProduct = {
  readonly id: string
  readonly name: string
}

export const ProductMultiPicker = ({
  value,
  onChange,
  queryKeyPrefix,
}: {
  value: readonly SelectedProduct[]
  onChange: (products: readonly SelectedProduct[]) => void
  /** Isola o cache entre telas que buscam com critérios diferentes. */
  queryKeyPrefix: string
}): ReactNode => {
  const [search, setSearch] = useState('')
  const term = useDebounced(search)

  const results = useQuery({
    queryKey: [queryKeyPrefix, term],
    queryFn: async ({ signal }) =>
      await fetchProducts(
        {
          status: 'active',
          // F247: o arquivado até ter estoque fica de fora da escolha.
          shelved: 'exclude',
          pageSize: 8,
          ...(term === '' ? {} : { search: term }),
        },
        signal
      ),
  })

  const chosen = new Set(value.map((product) => product.id))
  const items = (results.data?.items ?? []).filter(
    (item) => !chosen.has(item.id)
  )

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        value={null}
        onValueChange={(id) => {
          if (id === null) return
          const item = items.find((candidate) => candidate.id === id)
          if (item === undefined) return
          onChange([...value, { id: item.id, name: item.name }])
          setSearch('')
        }}
        items={items.map((item) => ({
          value: item.id,
          label: item.name,
          hint: item.brand ?? undefined,
        }))}
        search={search}
        onSearchChange={setSearch}
        selectedLabel={null}
        loading={results.isFetching}
        placeholder="Buscar produto para adicionar"
        emptyMessage={
          term === ''
            ? 'Digite para buscar.'
            : `Nenhum produto novo para "${term}".`
        }
      />

      {value.length === 0 ? null : (
        <ul className="flex flex-wrap gap-2">
          {value.map((product) => (
            <li key={product.id}>
              <span className="inline-flex items-center gap-1 rounded-(--radius-sm) bg-(--surface-sunken) py-1 pr-1 pl-2 text-(length:--text-sm)">
                {product.name}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remover ${product.name}`}
                  onClick={() => {
                    onChange(value.filter((other) => other.id !== product.id))
                  }}
                >
                  <X aria-hidden="true" className="h-3.5 w-3.5" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
